/**
 * Parsha Page
 * Shows list of files for a selected parsha
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, FileText } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';

const ParshaPage = () => {
  const { bookId, parshaName } = useParams();
  const navigate = useNavigate();
  const { manifest, loading, books, selectedBook, selectedParsha, setSelectedBook, setSelectedParsha, setSelectedFile } = useAppContext();
  const [isContactOpen, setIsContactOpen] = useState(false);
  useEffect(() => {
    // Wait until the manifest has loaded so books contain full parsha/file data.
    if (loading || !manifest) {
      return;
    }

    // Find and set the book and parsha based on URL parameters
    const book = books.find(b => b.id === bookId);
    if (!book) {
      navigate('/');
      return;
    }
    if (!selectedBook || selectedBook.id !== bookId) {
      setSelectedBook(book);
    }
    const decodedParshaName = decodeURIComponent(parshaName);
    const parsha = book.parshiot?.find(p => p.name.trim() === decodedParshaName.trim());
    
    if (!parsha) {
      navigate(`/book/${bookId}`);
      return;
    }

    if (!selectedParsha || selectedParsha.name !== parsha.name) {
      setSelectedParsha(parsha);
    }
  }, [bookId, parshaName, manifest, loading, books, selectedBook, selectedParsha, setSelectedBook, setSelectedParsha, navigate]);

  const handleFileClick = (file) => {
    setSelectedFile(file);
    navigate(`/book/${bookId}/parsha/${encodeURIComponent(parshaName)}/file/${encodeURIComponent(file)}`);
  };

  if (!selectedBook || !selectedParsha) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-white">טוען...</p>
      </div>
    );
  }

  const files = selectedParsha.files || [];

  const getFileDisplayName = (fileName) => {
    return fileName.replace('.html', '');
  };
  const handleGoBack = () => {
    window.history.length > 2 ? navigate(`/book/${bookId}`) : navigate('/');
  }

 return (
  <div className="min-h-screen flex flex-col">

    {/* TOP NAV BAR */}
    <div dir="rtl" className="w-full py-4 px-6 flex justify-between items-center">
      
      {/* Contact */}
      <div className="relative">
        <button 
          onClick={() => setIsContactOpen(!isContactOpen)}
          className="flex items-center gap-2 text-amber-300 hover:text-amber-400 transition font-semibold text-base"
        >
          <span>צור קשר</span>
          <span className={`text-[11px] transition-transform ${isContactOpen ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {isContactOpen && (
          <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl p-2 z-50 text-gray-800 border border-gray-200">
            <a href="mailto:tora2338@gmail.com" className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition">
              <span className="text-xl">📧</span>
              <div className="flex flex-col text-right">
                <span className="font-bold text-sm">דברו איתנו במייל</span>
                <span className="text-[11px] text-gray-500">להערות, הארות, לקבלת הדף השבועי במייל הקדשה לעילוי נשמת וכו׳</span>
              </div>
            </a>
            <a  
              href="https://links.payboxapp.com/iBta1etlyWb" 
              target="_blank" 
              rel="noreferrer" 
              className="flex items-center gap-3 p-3 hover:bg-orange-50 rounded-lg transition border-t mt-1"
            >
              <span className="text-xl">💰</span>
              <div className="flex flex-col text-right">
                <span className="font-bold text-sm text-orange-700">תרומה בפייבוקס</span>
                <span className="text-[11px] text-gray-500">עזרו לנו לגדול</span>
              </div>
            </a>
          </div>
        )}
      </div>

      {/* Logo / Title center */}
      <div className="absolute left-1/2 -translate-x-1/2 text-center hidden md:block">
        <span className="text-amber-300 font-bold text-lg md:text-xl tracking-wide">📖 הלכה בפרשה</span>
      </div>

      {/* App Link */}
      <a 
        href="https://play.google.com/store/apps/details?id=com.halachaparasha.app" 
        target="_blank" 
        rel="noreferrer" 
        className="font-semibold text-base text-amber-300 hover:text-amber-400 transition"
      >
        מעבר לאפליקציה 📱
      </a>
    </div>
    

    {/* Page Content */}
    <div className="space-y-6 max-w-6xl mx-auto px-4 w-full mt-6">

      {/* Back Button */}
      <div className="mb-2 w-full">
        <button
          onClick={() => handleGoBack()}
          className="flex items-center gap-1 text-amber-600 hover:text-amber-800 transition font-semibold text-lg md:text-xl "
        >
          ← חזור
        </button>
      </div>

      {/* Parsha Header */}
      <div className="bg-white rounded-2xl p-4 mb-4 shadow-lg border-2 border-amber-300">
        <h2 className="text-center text-gray-600">{selectedBook.name}</h2>
        <p className="text-2xl font-bold text-gray-800 text-center">פרשת {selectedParsha.name}</p>
        <p className="text-center text-gray-500 text-sm mt-2">דפים : {files.length}</p>
      </div>

      {/* File List */}
      <div dir="rtl" className="space-y-4">
        {files.map((file, index) => (
          <button
            key={index}
            onClick={() => handleFileClick(file)}
            className="w-full bg-white rounded-2xl p-5 shadow-lg hover:shadow-2xl transition-shadow duration-300 flex items-center justify-between border border-amber-200 hover:border-amber-300 hover:scale-[1.03] transform"
          >
            <ChevronLeft className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="flex-1 mx-4 text-right">
              <p className="text-lg font-semibold text-gray-900 leading-snug">
                {getFileDisplayName(file)}
              </p>
            </div>
            <FileText className="w-6 h-6 text-amber-500 flex-shrink-0" />
          </button>
        ))}
      </div>

      {/* No Files Info */}
      {files.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mt-8 text-center">
          <p className="text-gray-700 font-medium">
            אין קבצים זמינים לפרשה זו
          </p>
        </div>
      )}

      <div className="h-5"></div>
    </div>

  </div>
);

};

export default ParshaPage;